import React from 'react';
import { Badge } from '../UI/badge';
import { useLocale } from '../../contexts/LocaleContext';
import {
  displayOrDash,
  formatCompanyWebsiteHref,
  getRegionFromCountry,
  normalizeSupplierTier,
} from '../../utils/companyDisplayUtils';

function ProfileRow({ label, children }) {
  if (children == null || children === '' || children === '—') return null;
  return (
    <div className="pt-3 border-t border-border-light first:border-t-0 first:pt-0">
      <span className="text-xs font-medium uppercase tracking-wide text-text-tertiary">{label}</span>
      <div className="mt-2 text-sm text-text-primary">{children}</div>
    </div>
  );
}

function CompanySupplierProfileSection({ company }) {
  const { t } = useLocale();
  if (!company) return null;

  const tier = normalizeSupplierTier(company.tier);
  const tierLabel = tier ? t(`createSupplierTier${tier}`) : null;
  const region = company.region || getRegionFromCountry(company.country);
  const websiteHref = formatCompanyWebsiteHref(company.website);
  const categories = (company.categories || []).filter(Boolean);

  return (
    <div className="space-y-0">
      <ProfileRow label={t('createSupplierTickerLabel')}>
        <span className="font-mono text-sm">{displayOrDash(company.ticker)}</span>
      </ProfileRow>

      {tierLabel ? (
        <ProfileRow label={t('createSupplierTierLabel')}>
          <Badge variant="secondary">{tierLabel}</Badge>
        </ProfileRow>
      ) : null}

      {websiteHref ? (
        <div className="pt-3 border-t border-border-light">
          <span className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
            {t('companyOverviewWebsite')}
          </span>
          <a
            href={websiteHref}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex text-sm font-medium text-link hover:underline"
          >
            {company.website}
            <span className="ml-1" aria-hidden>
              ↗
            </span>
          </a>
        </div>
      ) : null}

      <ProfileRow label={t('companyOverviewDescription')}>
        <p className="leading-relaxed">{displayOrDash(company.description)}</p>
      </ProfileRow>

      <ProfileRow label={t('tenantProfileCountry')}>
        {displayOrDash(company.country)}
        {region ? (
          <span className="ml-2 text-text-secondary">
            · {t('companyOverviewRegion')}: {region}
          </span>
        ) : null}
      </ProfileRow>

      {company.legalName ? (
        <ProfileRow label={t('companyOverviewLegalName')}>{company.legalName}</ProfileRow>
      ) : null}

      {company.sector ? (
        <ProfileRow label={t('createSupplierSectorLabel')}>{company.sector}</ProfileRow>
      ) : null}

      {company.dunsNumber && company.dunsNumber !== 'N/A (pending)' ? (
        <ProfileRow label={t('companyOverviewDuns')}>{company.dunsNumber}</ProfileRow>
      ) : null}

      {company.annualSpend ? (
        <ProfileRow label={t('companyOverviewAnnualSpend')}>{company.annualSpend}</ProfileRow>
      ) : null}

      {company.criticalityScore != null && company.criticalityScore !== '' ? (
        <ProfileRow label={t('companyOverviewCriticality')}>
          {displayOrDash(company.criticalityScore)}
        </ProfileRow>
      ) : null}

      {categories.length > 0 ? (
        <ProfileRow label={t('companyOverviewCategories')}>
          <div className="flex flex-wrap gap-1.5">
            {categories.map((cat, i) => (
              <Badge key={i} variant="outline" className="text-xs font-normal">
                {cat}
              </Badge>
            ))}
          </div>
        </ProfileRow>
      ) : null}

      {company.keywords?.length > 0 ? (
        <ProfileRow label={t('companyOverviewKeywords')}>
          <div className="flex flex-wrap gap-1.5">
            {company.keywords.map((kw, i) => (
              <Badge key={i} variant="secondary" className="text-xs font-normal">
                {kw}
              </Badge>
            ))}
          </div>
        </ProfileRow>
      ) : (
        <ProfileRow label={t('companyOverviewKeywords')}>—</ProfileRow>
      )}

      {company.supplyChainRisks?.length > 0 ? (
        <ProfileRow label={t('companyOverviewSupplyChainRisks')}>
          <ul className="space-y-1.5">
            {company.supplyChainRisks.map((risk, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-brand-500">•</span>
                {risk}
              </li>
            ))}
          </ul>
        </ProfileRow>
      ) : null}

      {company.riskCategories?.length > 0 ? (
        <ProfileRow label={t('companyOverviewRiskCategories')}>
          <div className="flex flex-wrap gap-1.5">
            {company.riskCategories.map((cat, i) => (
              <Badge key={i} variant="outline" className="text-xs font-normal capitalize">
                {(cat || '').replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>
        </ProfileRow>
      ) : null}
    </div>
  );
}

export default CompanySupplierProfileSection;
